import { Link } from 'react-router-dom';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

import { MissionKindEnum } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import { PATH_MISSION_DEFAULT } from '../../../app/constants/paths.constant';
import { buildMissionPath } from '../../../app/utils/mission-space.util';
import { ConsultButton } from '../../../components/ConsultButton';
import { ActorListItem } from '../../../components/FolderBusinessEntityList/ActorListItem';
import { MissionSummary } from '../../../components/FolderBusinessEntityList/MissionSummary';
import { ServiceUsagesTotalContent } from '../../../components/ServiceUsages/TotalCard/TotalContent';
import { ConfidentialValue } from '../../../components/ConfidentialValue';
import { formatDateTime } from '../../../utils/format-date-time.util';
import type { ServiceUsagesTotalCardCmp_TotalCalculation_Fragment } from '../../../components/ServiceUsages/TotalCard';

import { QuotationFlowArrow } from './QuotationFlowArrow';
import type { SubMissionPricing } from '../types/sub-mission-pricing.type';

const DETAIL_LABEL = 'Détail du chiffrage';

interface Props {
  // Not to leave the pricing form in the edition mode.
  readonly consultInNewTab?: boolean;
  readonly detailPath: string;
  readonly subMission: SubMissionPricing;
  readonly totalCalculation: FragmentType<
    typeof ServiceUsagesTotalCardCmp_TotalCalculation_Fragment
  > | null;
}

export const SubMissionPricingCard = ({
  consultInNewTab = false,
  detailPath,
  subMission,
  totalCalculation,
}: Readonly<Props>) => {
  const { card } = subMission;

  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
      <CardContent
        sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}
      >
        <List sx={{ width: '100%' }}>
          {card.consolidation.quotationEmitterActors.map((actor) => (
            <ActorListItem
              actor={actor}
              avatarAlignment="end"
              key={actor.thirdPartyId}
            />
          ))}
          {card.consolidation.quotationEmitterActors.length > 0 &&
          card.consolidation.quotationReceiverActors.length > 0 ? (
            <QuotationFlowArrow />
          ) : null}
          {card.consolidation.quotationReceiverActors.map((actor) => (
            <ActorListItem
              actor={actor}
              avatarAlignment="start"
              key={actor.thirdPartyId}
            />
          ))}
        </List>
        {card.__typename === 'Mission' && <MissionSummary mission={card} />}
        <Typography variant="body2">
          Ouvert{' '}
          {formatDateTime(card.businessCreationDate, { datePrefix: 'le ' })}
        </Typography>
        {/* The totals of the cards of a row line up at their foot. */}
        <Box sx={{ mt: 'auto', pt: 2 }}>
          <Typography variant="overline">Total</Typography>
          {totalCalculation ? (
            <ServiceUsagesTotalContent totalCalculation={totalCalculation} />
          ) : (
            <ConfidentialValue />
          )}
        </Box>
      </CardContent>
      <CardActions disableSpacing>
        <ConsultButton
          inNewTab={consultInNewTab}
          to={buildMissionPath(PATH_MISSION_DEFAULT, {
            folderId: subMission.folderId,
            missionId: subMission._id,
            kind: MissionKindEnum.SubMission,
            compositeMissionId: subMission.compositeMissionId,
          })}
        />
        <Tooltip title={DETAIL_LABEL}>
          <IconButton
            aria-label={DETAIL_LABEL}
            component={Link}
            sx={{ ml: 'auto' }}
            to={detailPath}
          >
            <ReceiptLongIcon />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  );
};
